import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Button,
  Grid,
  Paper,
  LinearProgress,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Stack,
  Card,
  CardContent,
  Chip,
  Select,
  MenuItem,
  FormControl,
  TextField,
} from '@mui/material';
import { getAllModules } from '../config/modules';
import {
  AccountBalance,
  TrendingUp,
  People,
  Domain,
  Assessment,
  Business,
  Description,
  Event,
  Message,
  Speed,
  PersonAdd,
  EventAvailable,
  Payment,
  Notifications,
  Report,
  PersonOff,
  Receipt,
  CheckCircle,
  PendingActions,
  DateRange,
  Warning,
  School,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { getApiUrl } from '../config/api';
import AnalyticsCharts from '../components/dashboard/AnalyticsCharts';
import DashboardCharts from '../components/dashboard/DashboardCharts';
import FinanceDashboard from './FinanceDashboard';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const Dashboard = () => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedInstitution, setSelectedInstitution] = useState(null);
  const [breakdownTab, setBreakdownTab] = useState(0);
  const [showLogo, setShowLogo] = useState(true); // For logo fallback
  const location = useLocation();
  const preserveFilters = location.state?.preserveFilters;

  const [voucherFilter, setVoucherFilter] = useState(() => {
    if (preserveFilters) {
      const saved = sessionStorage.getItem('dashboard_voucherFilter');
      if (saved) return saved;
    } else {
      sessionStorage.removeItem('dashboard_voucherFilter');
    }
    const d = new Date();
    return `${d.getMonth() + 1}-${d.getFullYear()}`;
  });
  const [chartYearFilter, setChartYearFilter] = useState(() => new Date().getFullYear());
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState('all');
  
  // Generate cache-busting parameter once on mount to ensure fresh logo
  const [logoCacheBuster] = useState(() => `?t=${Date.now()}`);

  useEffect(() => {
    fetchDashboardStats();
    fetchClasses();

    // Load selected institution
    const institutionData = localStorage.getItem('selectedInstitution');
    if (institutionData) {
      try {
        setSelectedInstitution(JSON.parse(institutionData));
      } catch (e) {
        console.error('Failed to parse institution data', e);
      }
    }
  }, []);

  const fetchClasses = async () => {
    try {
      const token = localStorage.getItem('token');
      const institutionData = localStorage.getItem('selectedInstitution');
      let url = getApiUrl('classes');
      const params = {};
      if (institutionData) {
        try {
          const inst = JSON.parse(institutionData);
          params.institution = inst._id;
        } catch (e) {
          console.error('Failed to parse institution data', e);
        }
      }
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      if (response.data?.success) {
        setClasses(response.data.data || []);
      }
    } catch (e) {
      console.error('Failed to fetch classes', e);
    }
  };

  const handleClassChange = (e) => {
    const val = e.target.value;
    setSelectedClass(val);
    fetchDashboardStats(val);
  };

  const fetchDashboardStats = async (classId = selectedClass) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const institutionData = localStorage.getItem('selectedInstitution');

      // Build URL with institution parameter if available
      let url = getApiUrl('dashboard/stats');
      const queryParams = [];
      if (institutionData) {
        try {
          const institution = JSON.parse(institutionData);
          queryParams.push(`institution=${institution._id}`);
        } catch (e) {
          console.error('Failed to parse institution data', e);
        }
      }
      if (classId && classId !== 'all') {
        queryParams.push(`class=${classId}`);
      }
      if (queryParams.length > 0) {
        url += `?${queryParams.join('&')}`;
      }

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboardData(response.data.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch dashboard data');
      console.error('Dashboard error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleProfile = () => {
    handleClose();
    navigate('/profile');
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  // Get modules from shared configuration and convert icon components to elements
  const modules = getAllModules().map(module => ({
    ...module,
    icon: <module.icon />
  }));

  const handleModuleClick = (module) => {
    if (module.route) {
      navigate(module.route);
    } else {
      alert(`${module.name} module is coming soon!`);
    }
  };

  const StatCard = ({ title, value, icon, color, subtitle, trend, compact = false, onClick }) => (
    <Card
      onClick={onClick}
      elevation={0}
      sx={{
        cursor: onClick ? 'pointer' : 'default',
        background: `linear-gradient(135deg, ${color}10 0%, #ffffff 100%)`,
        border: `1px solid ${color}20`,
        borderRadius: 3,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        height: 'auto',
        minHeight: 80,
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
        '&:hover': {
          transform: onClick ? 'translateY(-6px)' : 'none',
          boxShadow: onClick ? `0 20px 40px ${color}15` : '0 4px 20px rgba(0,0,0,0.03)',
          border: onClick ? `1px solid ${color}40` : `1px solid ${color}20`,
        }
      }}
    >
      <CardContent sx={{ 
        p: compact ? 2.5 : 3.5, 
        '&:last-child': { pb: compact ? 2.5 : 3.5 },
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center'
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.7rem', opacity: 0.8 }}>
              {title}
            </Typography>
            <Typography variant={compact ? 'subtitle1' : 'h4'} fontWeight="800" color={color} sx={{ lineHeight: 1.3, wordBreak: 'break-word', fontSize: compact ? { xs: '1rem', sm: '1.1rem', md: '1.15rem' } : undefined }}>
              {value}
            </Typography>
            {(subtitle || trend) && (
              <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                {subtitle && <Typography component="div" variant="caption" color="text.secondary" sx={{ fontWeight: 500, fontSize: '0.75rem', width: '100%' }}>{subtitle}</Typography>}
                {trend && (
                  <Chip 
                    label={`+${trend}`}
                    size="small"
                    icon={<TrendingUp sx={{ fontSize: '12px !important', color: 'inherit !important' }} />}
                    sx={{ 
                      height: 20, 
                      fontSize: '0.7rem', 
                      fontWeight: 700,
                      bgcolor: 'success.main', 
                      color: 'white',
                      '& .MuiChip-icon': { ml: 0.5 }
                    }}
                  />
                )}
              </Box>
            )}
          </Box>
          <Box sx={{ 
            p: compact ? 1.5 : 2, 
            borderRadius: 3, 
            background: `linear-gradient(135deg, ${color} 0%, ${color}dd 100%)`,
            boxShadow: `0 8px 16px ${color}40`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}>
            {React.cloneElement(icon, { sx: { fontSize: compact ? 28 : 36, color: '#fff' } })}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  const MetricGroup = ({ title, metrics, color }) => (
    <Paper elevation={0} sx={{ 
      p: 2, 
      borderRadius: 4, 
      border: '1px solid #edf2f7', 
      pb: 1.5,
      boxShadow: '0 4px 12px rgba(0,0,0,0.02)',
      transition: 'all 0.3s ease',
      '&:hover': { 
        boxShadow: '0 12px 30px rgba(0,0,0,0.05)',
      }
    }}>
      <Typography variant="subtitle1" fontWeight="800" gutterBottom sx={{ color: 'text.primary', display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Box sx={{ width: 6, height: 24, bgcolor: color, borderRadius: 1 }} />
        {title}
      </Typography>
      <Grid container spacing={2}>
        {metrics.map((m, i) => (
          <Grid item xs={4} key={i}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 500, fontSize: '0.75rem' }}>{m.label}</Typography>
              <Typography variant="h6" fontWeight="800" color={color}>{m.value}</Typography>
            </Box>
          </Grid>
        ))}
      </Grid>
    </Paper>
  );

  const AlertItem = ({ title, count, icon, color, onClick }) => (
    <Box 
      onClick={onClick}
      sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 2, 
        p: 2, 
        borderRadius: 2, 
        cursor: 'pointer',
        bgcolor: `${color}08`, 
        border: `1px solid ${color}15`,
        transition: 'all 0.2s ease',
        '&:hover': { bgcolor: `${color}15`, transform: 'translateX(4px)' }
      }}
    >
      <Box sx={{ p: 1, borderRadius: 1.5, bgcolor: 'white', display: 'flex', color: color, boxShadow: '0 2px 6px rgba(0,0,0,0.05)' }}>
        {React.cloneElement(icon, { sx: { fontSize: 20 } })}
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography variant="body2" fontWeight="700" color="text.primary">{title}</Typography>
        <Typography variant="caption" color="text.secondary">{count} pending items</Typography>
      </Box>
      <Typography variant="h6" fontWeight="800" color={color}>{count}</Typography>
    </Box>
  );

  const EventItem = ({ event }) => (
    <Box sx={{ 
      display: 'flex', 
      gap: 2, 
      p: 2, 
      borderRadius: 2, 
      bgcolor: '#f8fafc',
      borderLeft: `4px solid ${event.color || '#667eea'}`,
      '&:hover': { bgcolor: '#f1f5f9' },
      transition: 'background 0.2s ease'
    }}>
      <Box sx={{ textAlign: 'center', minWidth: 50 }}>
        <Typography variant="caption" fontWeight="800" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
          {new Date(event.startDate).toLocaleString('default', { month: 'short' })}
        </Typography>
        <Typography variant="h6" fontWeight="800" color="text.primary" sx={{ lineHeight: 1 }}>
          {new Date(event.startDate).getDate()}
        </Typography>
      </Box>
      <Box>
        <Typography variant="subtitle2" fontWeight="700" color="text.primary">
          {event.title}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Event sx={{ fontSize: 14 }} /> {new Date(event.startDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          {event.location && ` • ${event.location}`}
        </Typography>
      </Box>
    </Box>
  );

  const searchParams = new URLSearchParams(location.search);
  const currentView = searchParams.get('view');

  if (user.role === 'finance_manager' || (user.role === 'super_admin' && currentView === 'finance')) {
    return <FinanceDashboard />;
  }

  const getActiveVoucherData = () => {
    if (!dashboardData?.vouchers) return null;
    if (['allTime', 'currentMonth', 'prevMonth'].includes(voucherFilter)) {
      return dashboardData.vouchers[voucherFilter];
    }
    const [month, year] = voucherFilter.split('-');
    const found = dashboardData.vouchers.monthlyBreakdown?.find(
      (m) => m._id.month === parseInt(month) && m._id.year === parseInt(year)
    );
    return found || { 
      total: 0, 
      paid: 0, 
      unpaid: 0, 
      partial: 0, 
      totalBilled: 0, 
      totalCollected: 0, 
      totalOutstanding: 0, 
      totalArrears: 0,
      collectedPaid: 0, 
      collectedPartial: 0, 
      outstandingRegular: 0, 
      outstandingArrears: 0 
    };
  };
  const activeVoucherData = getActiveVoucherData();

  const getVoucherChartData = () => {
    if (!dashboardData?.vouchers?.monthlyBreakdown) return [];
    
    const months = Array.from({ length: 12 }, (_, i) => i + 1);
    
    return months.map(month => {
      const found = dashboardData.vouchers.monthlyBreakdown.find(
        m => m._id.year === chartYearFilter && m._id.month === month
      );
      const date = new Date(chartYearFilter, month - 1, 1);
      return {
        name: date.toLocaleDateString('en-US', { month: 'short' }),
        Total: found ? found.total : 0,
        Paid: found ? found.paid : 0,
        Partial: found ? found.partial : 0,
        Unpaid: found ? found.unpaid : 0
      };
    });
  };
  const voucherChartData = getVoucherChartData();

  if (loading) {
    return (
      <Box sx={{ minHeight: '60vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {dashboardData && (
        <>
          {/* Quick Access to Finance Dashboard for Super Admin */}
          {user.role === 'super_admin' && (
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                mb: 3,
                borderRadius: 3,
                background: 'linear-gradient(135deg, #10b98115 0%, #05966905 100%)',
                border: '1px solid #10b98130',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 2,
                boxShadow: '0 4px 12px rgba(16, 185, 129, 0.05)',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  sx={{
                    bgcolor: '#10b981',
                    color: '#fff',
                    p: 1.25,
                    borderRadius: 2.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)',
                  }}
                >
                  <AccountBalance sx={{ fontSize: 26 }} />
                </Box>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#065f46', lineHeight: 1.2 }}>
                    Finance Manager Dashboard
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem', mt: 0.25 }}>
                    Monitor multi-campus collections, admission fee insights, and consolidated bank statement reconciliations.
                  </Typography>
                </Box>
              </Box>
              <Button
                variant="contained"
                endIcon={<TrendingUp />}
                onClick={() => navigate('/finance-dashboard')}
                sx={{
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  color: '#fff',
                  fontWeight: 700,
                  borderRadius: 2.5,
                  px: 2.5,
                  py: 1,
                  textTransform: 'none',
                  boxShadow: '0 4px 12px rgba(16, 185, 129, 0.25)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                    transform: 'translateY(-1px)',
                    boxShadow: '0 6px 16px rgba(16, 185, 129, 0.35)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                Go to Finance Manager Dashboard
              </Button>
            </Paper>
          )}

          {/* Academic Setup Bar */}
          <Paper 
            elevation={0}
            sx={{ 
              p: 2, 
              mb: 3, 
              borderRadius: 3,
              background: 'linear-gradient(135deg, #667eea15 0%, #764ba205 100%)', 
              border: '1px solid #667eea30',
              boxShadow: '0 4px 12px rgba(102, 126, 234, 0.05)'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#2c3e50' }}>
                Academic Setup
              </Typography>
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => navigate('/admissions/new')}
                  sx={{
                    borderColor: '#10b981',
                    color: '#10b981',
                    fontWeight: 600,
                    borderRadius: 2,
                    px: 2,
                    py: 0.75,
                    '&:hover': {
                      borderColor: '#059669',
                      bgcolor: '#10b98115',
                    },
                  }}
                >
                  ➕ New Admission
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => navigate('/classes')}
                  sx={{
                    borderColor: '#667eea',
                    color: '#667eea',
                    fontWeight: 600,
                    borderRadius: 2,
                    px: 2,
                    py: 0.75,
                    '&:hover': {
                      borderColor: '#5568d3',
                      bgcolor: '#667eea15',
                    },
                  }}
                >
                  📚 Manage Classes
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => navigate('/sections')}
                  sx={{
                    borderColor: '#764ba2',
                    color: '#764ba2',
                    fontWeight: 600,
                    borderRadius: 2,
                    px: 2,
                    py: 0.75,
                    '&:hover': {
                      borderColor: '#653a8b',
                      bgcolor: '#764ba215',
                    },
                  }}
                >
                  📑 Manage Sections
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => navigate('/groups')}
                  sx={{
                    borderColor: '#f093fb',
                    color: '#f093fb',
                    fontWeight: 600,
                    borderRadius: 2,
                    px: 2,
                    py: 0.75,
                    '&:hover': {
                      borderColor: '#d97ee4',
                      bgcolor: '#f093fb15',
                    },
                  }}
                >
                  👥 Manage Groups
                </Button>
                {user.role === 'super_admin' && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => navigate('/finance-dashboard')}
                    startIcon={<AccountBalance sx={{ fontSize: 16 }} />}
                    sx={{
                      borderColor: '#10b981',
                      color: '#059669',
                      fontWeight: 700,
                      borderRadius: 2,
                      px: 2,
                      py: 0.75,
                      textTransform: 'none',
                      bgcolor: '#10b9810a',
                      '&:hover': {
                        borderColor: '#059669',
                        bgcolor: '#10b98118',
                      },
                    }}
                  >
                    Finance Dashboard
                  </Button>
                )}
              </Box>
            </Box>
          </Paper>

          {/* Row 1: Key Performance Indicators (Compact) */}
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }, gap: 3, width: '100%', mb: 4 }}>
                {[
                  { 
                    title: 'Total Students', 
                    value: dashboardData?.users?.roleBreakdown?.students || 0, 
                    icon: <People />, 
                    color: '#4facfe', 
                    subtitle: 'Active enrollments',
                    path: '/admissions/register'
                  },
                  { 
                    title: 'New Admissions', 
                    value: dashboardData?.administrative?.newAdmissions || 0, 
                    icon: <PersonAdd />, 
                    color: '#6366f1', 
                    subtitle: 'Last 30 days',
                    path: '/admissions/register?days=30'
                  },
                  { 
                    title: 'Enrolled Students', 
                    value: dashboardData?.administrative?.enrolledStudents || 0, 
                    icon: <CheckCircle />, 
                    color: '#10b981', 
                    subtitle: 'Currently enrolled',
                    path: '/admissions/register?status=enrolled'
                  },
                  { 
                    title: 'Struck Off Students', 
                    value: dashboardData?.administrative?.struckOffStudents || 0, 
                    icon: <PersonOff />, 
                    color: '#ff6b6b', 
                    subtitle: 'Total struck off',
                    path: '/admissions/register?status=struck_off'
                  }
                ].map((stat, i) => (
                  <StatCard key={i} compact {...stat} onClick={() => stat.path && navigate(stat.path)} />
                ))}
              </Box>

              {/* Row 2: Financial & Voucher Overview */}
              {activeVoucherData && (
                <Box sx={{ mb: 4, width: '100%' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h5" fontWeight="bold">
                      Financial & Voucher Overview
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Typography variant="button" color="text.secondary" fontWeight="bold">
                        BILLING MONTH
                      </Typography>
                      <TextField
                        type="month"
                        size="small"
                        value={(() => {
                          if (['allTime', 'currentMonth', 'prevMonth'].includes(voucherFilter)) {
                            const d = new Date();
                            return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
                          }
                          const [m, y] = voucherFilter.split('-');
                          return `${y}-${String(m).padStart(2, '0')}`;
                        })()}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val) {
                            const [y, m] = val.split('-');
                            const newFilter = `${parseInt(m, 10)}-${parseInt(y, 10)}`;
                            setVoucherFilter(newFilter);
                            sessionStorage.setItem('dashboard_voucherFilter', newFilter);
                          }
                        }}
                        sx={{ 
                          bgcolor: 'white', 
                          borderRadius: 2, 
                          width: 180,
                          '& .MuiOutlinedInput-root': { borderRadius: 2 } 
                        }}
                      />
                    </Box>
                  </Box>
                  
                  {/* Financial KPI Cards */}
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }, gap: 3, width: '100%', mb: 4 }}>
                  {[
                    { 
                      title: 'Total Billed', 
                      value: `Rs. ${(activeVoucherData.totalBilled || 0).toLocaleString()}`, 
                      icon: <AccountBalance />, 
                      color: '#6366f1', 
                      subtitle: (
                        <Box sx={{ display: 'flex', gap: 1.5, mt: 0.5, flexWrap: 'wrap', width: '100%' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#6366f1' }} />
                            <span>Vouchers: Rs. {((activeVoucherData.totalBilled || 0) - (activeVoucherData.totalArrears || 0)).toLocaleString()}</span>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#f59e0b' }} />
                            <span>Arrears: Rs. {(activeVoucherData.totalArrears || 0).toLocaleString()}</span>
                          </Box>
                        </Box>
                      ),
                      path: `/fee-management?tab=print-voucher&month=${(() => {
                        if (['allTime', 'currentMonth', 'prevMonth'].includes(voucherFilter)) {
                          const d = new Date();
                          if (voucherFilter === 'prevMonth') d.setMonth(d.getMonth() - 1);
                          return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
                        }
                        const [m, y] = voucherFilter.split('-');
                        return `${y}-${String(m).padStart(2, '0')}`;
                      })()}`
                    },
                    { 
                      title: 'Total Collected', 
                      value: `Rs. ${(activeVoucherData.totalCollected || 0).toLocaleString()}`, 
                      icon: <Payment />, 
                      color: '#10b981', 
                      subtitle: (
                        <Box sx={{ display: 'flex', gap: 1.5, mt: 0.5, flexWrap: 'wrap', width: '100%' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#34d399' }} />
                            <span>Paid: Rs. {(activeVoucherData.collectedPaid || 0).toLocaleString()}</span>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#f59e0b' }} />
                            <span>Partial: Rs. {(activeVoucherData.collectedPartial || 0).toLocaleString()}</span>
                          </Box>
                        </Box>
                      ),
                      path: `/fee-management?tab=print-voucher&status=Paid&month=${(() => {
                        if (['allTime', 'currentMonth', 'prevMonth'].includes(voucherFilter)) {
                          const d = new Date();
                          if (voucherFilter === 'prevMonth') d.setMonth(d.getMonth() - 1);
                          return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
                        }
                        const [m, y] = voucherFilter.split('-');
                        return `${y}-${String(m).padStart(2, '0')}`;
                      })()}`
                    },
                    { 
                      title: 'Total Outstanding', 
                      value: `Rs. ${(activeVoucherData.totalOutstanding || 0).toLocaleString()}`, 
                      icon: <Assessment />, 
                      color: '#ef4444', 
                      subtitle: (
                        <Box sx={{ display: 'flex', gap: 1.5, mt: 0.5, flexWrap: 'wrap', width: '100%' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#ef4444' }} />
                            <span>Vouchers: Rs. {(activeVoucherData.outstandingRegular || 0).toLocaleString()}</span>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#f59e0b' }} />
                            <span>Arrears: Rs. {(activeVoucherData.outstandingArrears || 0).toLocaleString()}</span>
                          </Box>
                        </Box>
                      ),
                      path: `/fee-management?tab=print-voucher&status=Unpaid&month=${(() => {
                        if (['allTime', 'currentMonth', 'prevMonth'].includes(voucherFilter)) {
                          const d = new Date();
                          if (voucherFilter === 'prevMonth') d.setMonth(d.getMonth() - 1);
                          return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
                        }
                        const [m, y] = voucherFilter.split('-');
                        return `${y}-${String(m).padStart(2, '0')}`;
                      })()}`
                    },
                    { 
                      title: 'Arrears', 
                      value: `Rs. ${(activeVoucherData.totalArrears || 0).toLocaleString()}`, 
                      icon: <Receipt />, 
                      color: '#f59e0b', 
                      subtitle: 'Outstanding arrears',
                      path: `/fee-management?tab=print-voucher&month=${(() => {
                        if (['allTime', 'currentMonth', 'prevMonth'].includes(voucherFilter)) {
                          const d = new Date();
                          if (voucherFilter === 'prevMonth') d.setMonth(d.getMonth() - 1);
                          return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
                        }
                        const [m, y] = voucherFilter.split('-');
                        return `${y}-${String(m).padStart(2, '0')}`;
                      })()}`
                    }
                  ].map((stat, i) => (
                    <StatCard key={`fin-${i}`} compact {...stat} onClick={() => stat.path && navigate(stat.path)} />
                  ))}
                  </Box>

                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 1 }}>
                    Voucher Overview
                  </Typography>

                  {/* Voucher KPI Cards */}
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }, gap: 3, width: '100%', mb: 3 }}>
                  {[
                    { 
                      title: 'Total Vouchers', 
                      value: activeVoucherData.total || 0, 
                      icon: <Receipt />, 
                      color: '#6366f1', 
                      subtitle: 'Generated',
                      path: `/fee-management?tab=print-voucher&month=${(() => {
                        if (['allTime', 'currentMonth', 'prevMonth'].includes(voucherFilter)) {
                          const d = new Date();
                          if (voucherFilter === 'prevMonth') d.setMonth(d.getMonth() - 1);
                          return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
                        }
                        const [m, y] = voucherFilter.split('-');
                        return `${y}-${String(m).padStart(2, '0')}`;
                      })()}`
                    },
                    { 
                      title: 'Paid Vouchers', 
                      value: activeVoucherData.paid || 0, 
                      icon: <CheckCircle />, 
                      color: '#34d399', 
                      subtitle: 'Fully paid',
                      path: `/fee-management?tab=print-voucher&status=Paid&month=${(() => {
                        if (['allTime', 'currentMonth', 'prevMonth'].includes(voucherFilter)) {
                          const d = new Date();
                          if (voucherFilter === 'prevMonth') d.setMonth(d.getMonth() - 1);
                          return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
                        }
                        const [m, y] = voucherFilter.split('-');
                        return `${y}-${String(m).padStart(2, '0')}`;
                      })()}`
                    },
                    { 
                      title: 'Unpaid Vouchers', 
                      value: activeVoucherData.unpaid || 0, 
                      icon: <Warning />, 
                      color: '#ef4444', 
                      subtitle: 'No payment made',
                      path: `/fee-management?tab=print-voucher&status=Unpaid&month=${(() => {
                        if (['allTime', 'currentMonth', 'prevMonth'].includes(voucherFilter)) {
                          const d = new Date();
                          if (voucherFilter === 'prevMonth') d.setMonth(d.getMonth() - 1);
                          return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
                        }
                        const [m, y] = voucherFilter.split('-');
                        return `${y}-${String(m).padStart(2, '0')}`;
                      })()}`
                    },
                    { 
                      title: 'Partially Paid', 
                      value: activeVoucherData.partial || 0, 
                      icon: <PendingActions />, 
                      color: '#f59e0b', 
                      subtitle: 'Some payment made',
                      path: `/fee-management?tab=print-voucher&status=Partial&month=${(() => {
                        if (['allTime', 'currentMonth', 'prevMonth'].includes(voucherFilter)) {
                          const d = new Date();
                          if (voucherFilter === 'prevMonth') d.setMonth(d.getMonth() - 1);
                          return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
                        }
                        const [m, y] = voucherFilter.split('-');
                        return `${y}-${String(m).padStart(2, '0')}`;
                      })()}`
                    }
                  ].map((stat, i) => (
                    <StatCard key={`vouch-${i}`} compact {...stat} onClick={() => stat.path && navigate(stat.path)} />
                  ))}
                  </Box>

                  {/* Voucher Trend Chart */}
                  {voucherChartData.length > 0 && (
                    <Paper elevation={0} sx={{ mt: 3, p: 3, borderRadius: 4, border: '1px solid #edf2f7' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="subtitle1" fontWeight="800">Monthly Voucher Trend</Typography>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <FormControl size="small" sx={{ minWidth: 150 }}>
                            <Select
                              value={selectedClass}
                              onChange={handleClassChange}
                              displayEmpty
                              sx={{ bgcolor: '#f8fafc', borderRadius: 2 }}
                            >
                              <MenuItem value="all">All Classes</MenuItem>
                              {classes.map((cls) => (
                                <MenuItem key={cls._id} value={cls._id}>{cls.name}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <FormControl size="small" sx={{ minWidth: 100 }}>
                            <Select
                              value={chartYearFilter}
                              onChange={(e) => setChartYearFilter(e.target.value)}
                              sx={{ bgcolor: '#f8fafc', borderRadius: 2 }}
                            >
                              {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(year => (
                                <MenuItem key={year} value={year}>{year}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Stack>
                      </Box>
                      <ResponsiveContainer width="99%" height={300}>
                        <BarChart data={voucherChartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                          <XAxis dataKey="name" axisLine={false} tickLine={false} style={{ fontSize: '11px', fontWeight: 500, fill: '#64748b' }} />
                          <YAxis axisLine={false} tickLine={false} style={{ fontSize: '11px', fontWeight: 500, fill: '#64748b' }} />
                          <Tooltip 
                            contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' }}
                            itemSorter={(item) => {
                              const order = { Total: 1, Paid: 2, Partial: 3, Unpaid: 4 };
                              return order[item.name] || 99;
                            }}
                          />
                          <Legend iconType="circle" wrapperStyle={{ fontSize: '12px', fontWeight: 600, paddingTop: '10px' }} />
                          <Bar dataKey="Total" fill="#6366f1" radius={[4, 4, 0, 0]} maxBarSize={40} />
                          <Bar dataKey="Paid" fill="#34d399" radius={[4, 4, 0, 0]} maxBarSize={40} />
                          <Bar dataKey="Partial" fill="#f59e0b" radius={[4, 4, 0, 0]} maxBarSize={40} />
                          <Bar dataKey="Unpaid" fill="#ef4444" radius={[4, 4, 0, 0]} maxBarSize={40} />
                        </BarChart>
                      </ResponsiveContainer>
                    </Paper>
                  )}
                </Box>
              )}

              {/* Row 4: Quick Actions (Full-Width Responsive Grid) */}
              <Box sx={{ mb: 4, width: '100%' }}>
                <Paper elevation={0} sx={{ p: 3, borderRadius: 4, border: '1px solid #edf2f7', pb: 3, width: '100%', boxSizing: 'border-box' }}>
                  <Typography variant="subtitle1" fontWeight="800" sx={{ mb: 3 }}>Quick Actions</Typography>
                  <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                    gap: 2,
                    width: '100%'
                  }}>
                    {[
                      { label: 'Register Student', icon: <PersonAdd />, color: '#667eea', path: '/admissions/new' },
                      { label: 'Generate Voucher', icon: <Receipt />, color: '#8b5cf6', path: '/fee-management?tab=voucher-generation' },
                      { label: 'Collect Fee', icon: <Payment />, color: '#10b981', path: '/fee-management?tab=fee-deposit' },
                      { label: 'Add Event', icon: <Event />, color: '#f093fb', path: '/calendar' },
                      { label: 'Manage Users', icon: <People />, color: '#4facfe', path: '/users' },
                      { label: 'Manage Classes', icon: <School />, color: '#8b5cf6', path: '/classes' },
                      { label: 'View Results', icon: <Assessment />, color: '#f59e0b', path: '/results' }
                    ].map((action, i) => (
                      <Button
                        key={i}
                        fullWidth
                        onClick={() => navigate(action.path)}
                        variant="outlined"
                        sx={{
                          flexDirection: 'column',
                          py: 2.5,
                          borderRadius: 3,
                          borderColor: `${action.color}20`,
                          bgcolor: `${action.color}08`,
                          color: 'text.primary',
                          gap: 1.5,
                          boxShadow: '0 4px 12px rgba(0,0,0,0.02)',
                          transition: 'all 0.3s ease',
                          width: '100%',
                          '&:hover': { 
                            bgcolor: `${action.color}15`, 
                            borderColor: action.color,
                            transform: 'translateY(-3px)',
                            boxShadow: `0 8px 24px ${action.color}20`
                          }
                        }}
                      >
                        {React.cloneElement(action.icon, { sx: { color: action.color, fontSize: 24 } })}
                        <Typography variant="caption" fontWeight="700" sx={{ textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', width: '100%' }}>
                          {action.label}
                        </Typography>
                      </Button>
                    ))}
                  </Box>
                </Paper>
              </Box>

              {/* Row 6: Comprehensive Analytics Charts */}
              <Box sx={{ mt: 4, mb: 2, width: '100%' }}>
                <DashboardCharts />
              </Box>
            </>
          )}
        </Box>
  );
};

export default Dashboard;